/**
 * The show's colors, one meaning each. The night is deep indigo; the beam
 * (one token's working memory) is cool cyan-white on purpose, because warm
 * means math is being done and nothing else; gold is the ten global blocks
 * and what they keep; the drafter is a matte violet, a different kind of
 * model; dawn is the finale's light.
 *
 * Hex values are sRGB as designed; `C` holds them in the linear light the
 * shaders and the drawing work in, `CSS` as they go on the page.
 */
import type { RGB } from "~/components/DeepDive/engine/math";

const HEX = {
  zenith: "#060A1F",
  horizon: "#101A44",
  grid: "#26366F",
  pulse: "#6E8CFF",
  starNew: "#E8F4FF",
  star: "#BFE3FF",
  starOld: "#4E6C99",
  glint: "#7FB6FF",
  gold: "#FFD27A",
  beam: "#EAFBFF",
  beamBody: "#9FE8FF",
  trail: "#56C8FF",
  heat: "#FFA24D",
  hot: "#FF7A2E",
  weights: "#1E2A4A",
  weightsEdge: "#5C79A8",
  drafter: "#8B8FC7",
  ghost: "#C7A9FF",
  line: "#9FB6E8",
  winner: "#FFE28A",
  /** The end token, in the series' colour for it (DeepDive's and Neon City's). */
  end: "#FF7F96",
  sun: "#FFE9B8",
  dawnHorizon: "#FFB277",
  dawnSky: "#2E4A9E"
} as const;

const linear = (x: number) => (x <= 0.04045 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4));
const rgb = (hex: string): RGB => [1, 3, 5].map((i) => linear(parseInt(hex.slice(i, i + 2), 16) / 255)) as RGB;

export type Swatch = keyof typeof HEX;

export const C = Object.fromEntries(Object.entries(HEX).map(([k, v]) => [k, rgb(v)])) as Record<Swatch, RGB>;
export const CSS = HEX;

/** A color brighter than itself, for the bloom to pick up: `k` times, past white where it glows. */
export const glow = (c: RGB, k: number): RGB => [c[0] * k, c[1] * k, c[2] * k];

/**
 * Numbers, wherever a vector is drawn as cells (the working memory, the stores' records, the unit's inputs): one
 * diverging scale from -1 to 1, rose and violet below zero, dark navy at it, cyan and mint above. Cool and vivid,
 * never gold or orange, which mean the hour marks and math.
 */
const VALUE_HEX = ["#FF4FA0", "#8A5CFF", "#13284A", "#3FD8FF", "#B8FFE0"];
const VALUE = VALUE_HEX.map(rgb);

/** The color of a number in -1..1. */
export function valueColor(v: number): RGB {
  const x = (Math.max(-1, Math.min(1, v)) + 1) * 2;
  const i = Math.min(3, Math.floor(x));
  const f = x - i;
  return [0, 1, 2].map((k) => VALUE[i][k] + (VALUE[i + 1][k] - VALUE[i][k]) * f) as RGB;
}

/** The same scale in GLSL: `vec3 valueColor(float v)`. */
export const VALUE_GLSL = /* glsl */ `
  vec3 valueColor(float v) {
    float x = (clamp(v, -1.0, 1.0) + 1.0) * 2.0;
    ${VALUE.map((c, i) => `const vec3 V${i} = vec3(${c.map((n) => n.toFixed(5)).join(", ")});`).join("\n    ")}
    if (x < 1.0) return mix(V0, V1, x);
    if (x < 2.0) return mix(V1, V2, x - 1.0);
    if (x < 3.0) return mix(V2, V3, x - 2.0);
    return mix(V3, V4, min(x - 3.0, 1.0));
  }
`;
